import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { Box } from "@mui/system";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ValuationHubCoreModelImage,
  useDeleteServiceApiValuationPackageValuationImageByIdMutation,
  usePostServiceApiValuationPackageValuationByValuationPackageItemIdImageMutation,
} from "@/api/generatedApi";
import { CloudUploadIcon } from "@/assets/icons/CloudUploadIcon";
import { ImageUpload } from "@/components/imageUpload";
import { updatePropertyDetails } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { PropertyInformation } from "@/types/refineRealEstate";

import { StyledFormGridContainer } from "../refineRealEstate.styled";
import { ImageDragPreview } from "./ImageDragPreview";
import { SortableImageCard } from "./SortableImageCard";

const styles = {
  galleryContainer: {
    display: "grid",
    gridColumn: "span 2",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: 2,
    minHeight: "270px",
  },
  locationIcon: {
    fontSize: "18px",
  },
};

export interface ImagesGalleryProps {
  readonly?: boolean;
  images: ValuationHubCoreModelImage[];
  onReorder?: (newOrder: string[]) => void;
  valuationId?: string | null;
  pendingImages?: Array<{
    id: string;
    file: File;
    preview: string;
    title: string;
  }>;
  setPendingImages?: React.Dispatch<
    React.SetStateAction<
      Array<{
        id: string;
        file: File;
        preview: string;
        title: string;
      }>
    >
  >;
}

export const ImagesGallery = ({
  readonly,
  images,
  onReorder,
  valuationId,
  pendingImages,
  setPendingImages,
}: ImagesGalleryProps): JSX.Element => {
  const { t } = useTranslation("RealEstateRefinement");
  const { t: tCommon } = useTranslation();
  const propertyInformation = useAppSelector((state) => state.currentValuation.propertyInformation);
  const [items, setItems] = useState<ValuationHubCoreModelImage[]>(images);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { setValue } = useFormContext<PropertyInformation>();
  const dispatch = useAppDispatch();
  const [uploadImage, { isLoading: isUploading }] =
    usePostServiceApiValuationPackageValuationByValuationPackageItemIdImageMutation();
  const [deleteImage, { isLoading: isDeleting }] = useDeleteServiceApiValuationPackageValuationImageByIdMutation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const maxFileSize = 10;
  const isUploadDisabled = items.length >= 12 || readonly;

  useEffect(() => {
    const allImages: ValuationHubCoreModelImage[] = [
      ...images,
      ...(pendingImages?.map((img) => ({
        id: img.id,
        url: img.preview,
        title: img.title,
      })) || []),
    ].filter((img) => img.id);

    setItems(allImages);
  }, [images, pendingImages]);

  const handleUpload = async (file: string | null): Promise<void> => {
    if (!file) {
      console.error("No file provided");
      return;
    }

    setUploadError(null);

    // For new valuations without ID, store in pending images
    if (!valuationId && setPendingImages) {
      try {
        const response = await fetch(file);
        const blob = await response.blob();
        const fileObj = new File([blob], `image.jpg`, { type: blob.type });

        const newPendingImage = {
          id: `temp-${Date.now()}`,
          file: fileObj,
          preview: file, // Use the base64 string as preview
          title: `${t("images.image")} ${(pendingImages?.length || 0) + images.length + 1}`,
        };

        setPendingImages((prev) => [...(prev || []), newPendingImage]);
        setImage(null);
        return;
      } catch (error) {
        console.error("Failed to create pending image:", error);
        setUploadError(tCommon("errors.loading.loadError"));
        return;
      }
    }

    if (!valuationId) {
      console.error("No valuation ID available for image upload");
      return;
    }

    try {
      const response = await fetch(file);
      const blob = await response.blob();

      const formData = new FormData();
      const extension = blob.type.split("/")[1] || "jpg";
      formData.append("File", blob, `image.${extension}`);
      formData.append("Title", `${t("images.image")} ${items.length + 1}`);

      const result = await uploadImage({
        valuationPackageItemId: valuationId,
        body: formData as { File?: Blob; Title?: string },
      }).unwrap();

      if (result) {
        const newImage: ValuationHubCoreModelImage = {
          id: result.id ?? "",
          url: result.url ?? "",
          title: result.title ?? "",
        };
        const updatedImages = [...items, newImage];
        setValue("propertyImages.images", updatedImages, { shouldDirty: true });
        setItems(updatedImages);
        setImage(null);

        if (propertyInformation) {
          dispatch(
            updatePropertyDetails({
              ...propertyInformation,
              propertyImages: {
                ...propertyInformation.propertyImages,
                images: updatedImages,
              },
            }),
          );
        }
      }
    } catch (error) {
      console.error("Failed to upload image:", error);
      setUploadError(tCommon("errors.loading.loadError"));
    }
  };

  const handleTitleChange = (imageId: string, newTitle: string): void => {
    if (imageId.startsWith("temp-") && setPendingImages) {
      setPendingImages((prev) => (prev || []).map((img) => (img.id === imageId ? { ...img, title: newTitle } : img)));
      setItems((prev) => prev.map((img) => (img.id === imageId ? { ...img, title: newTitle } : img)));
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (isDeleting || readonly) return;

    if (!id) {
      console.error("No image ID provided for deletion");
      return;
    }

    // Check if it's a pending image
    if (id.startsWith("temp-") && setPendingImages) {
      setPendingImages((prev) => (prev || []).filter((img) => img.id !== id));
      setItems((prev) => prev.filter((img) => img.id !== id));
      return;
    }

    try {
      await deleteImage({ id }).unwrap();

      const updatedImages = items.filter((img) => img.id !== id);
      setValue("propertyImages.images", updatedImages, { shouldDirty: true });
      setItems(updatedImages);

      if (propertyInformation) {
        dispatch(
          updatePropertyDetails({
            ...propertyInformation,
            propertyImages: {
              ...propertyInformation.propertyImages,
              images: updatedImages,
            },
          }),
        );
      }
    } catch (error) {
      console.error("Failed to delete image:", error);
    }
  };

  const handleDragStart = (event: DragStartEvent): void => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    if (readonly) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        const regularImages = newItems.filter((img) => !img.id?.startsWith("temp-"));
        const reorderedPendingImages = newItems.filter((img) => img.id?.startsWith("temp-"));

        const reorderedFormData = regularImages.map((img) => ({
          id: img.id,
          title: img.title,
          url: img.url,
        }));
        setValue("propertyImages.images", reorderedFormData, { shouldDirty: true });

        if (setPendingImages && reorderedPendingImages.length > 0) {
          setPendingImages((prev) => {
            const pendingMap = new Map((prev || []).map((img) => [img.id, img]));
            return reorderedPendingImages.map((img) => pendingMap.get(img.id!)).filter(Boolean) as Array<{
              id: string;
              file: File;
              preview: string;
              title: string;
            }>;
          });
        }

        if (onReorder) {
          onReorder(regularImages.map((item) => item.id).filter((id): id is string => id !== undefined));
        }

        return newItems;
      });
    }

    setActiveId(null);
  };

  const activeImage = activeId ? items.find((item) => item.id === activeId) : null;

  const getImageChipLabel = (index: number): string | undefined => {
    if (index === 0) {
      return t("images.titleImage");
    }
    if (index <= 3) {
      return `${t("images.highlight")} ${index}`;
    }
    return undefined;
  };

  return (
    <StyledFormGridContainer>
      <Box sx={styles.galleryContainer}>
        {!readonly && (
          <ImageUpload
            uploadIcon={<CloudUploadIcon sx={{ fontSize: "40px" }} />}
            uploadTypesText={t("images.uploadTypes", { maxFileSize })}
            titleMain={isUploading ? t("images.uploading") : t("images.clickToUpload")}
            titleSub={isUploading ? "" : t("images.dragAndDrop")}
            removeImageText={t("images.removeImage")}
            onImageUpload={isUploading ? (): void => {} : handleUpload}
            value={image}
            height={"100%"}
            disabled={isUploadDisabled}
            maxFileSize={maxFileSize}
            error={uploadError}
          />
        )}
        {(images.length > 0 || (pendingImages && pendingImages.length > 0)) && (
          <DndContext sensors={readonly ? [] : sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext
              items={items.map((img) => img.id).filter((id): id is string => id !== undefined)}
              strategy={rectSortingStrategy}
            >
              {items.map((image, index) => (
                <SortableImageCard
                  key={image.id}
                  image={image}
                  index={index}
                  chip={getImageChipLabel(index)}
                  isPending={image.id?.startsWith("temp-") ?? false}
                  onDelete={handleDelete}
                  onTitleChange={handleTitleChange}
                  minHeight="200px"
                  readonly={readonly}
                />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeImage ? (
                <Box
                  sx={{
                    width: "212px",
                    borderRadius: 1,
                    boxShadow: 3,
                    bgcolor: "white",
                  }}
                >
                  <ImageDragPreview image={activeImage} minHeight="120px" maxHeight="180px" />
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Box>
    </StyledFormGridContainer>
  );
};
